import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import SummaryApi from '../common/index';
import { S3Client, PutObjectCommand } from "@aws-sdk/client-s3";

// Initialize S3 client
const s3 = new S3Client({
  region: process.env.REACT_APP_AWS_REGION,
  credentials: {
    accessKeyId: process.env.REACT_APP_AWS_ACCESS_KEY_ID,
    secretAccessKey: process.env.REACT_APP_AWS_SECRET_ACCESS_KEY,
  },
});

// Function to upload resume to S3
const uploadResumeToS3 = async (file) => {
  if (!file) return null;

  const fileName = `resumes/${Date.now()}-${file.name}`;
  const uploadParams = {
    Bucket: "shivaytech-data", 
    Key: fileName,
    Body: await file.arrayBuffer(),
    ContentType: file.type,
  };

  try {
    await s3.send(new PutObjectCommand(uploadParams));
    return {
      url: `https://${uploadParams.Bucket}.s3.amazonaws.com/${fileName}`,
      originalName: file.name
    };
  } catch (error) {
    console.error("Resume Upload Error:", error);
    return null;
  }
};

const Career = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedJob, setSelectedJob] = useState(null);
  const [showJobDetails, setShowJobDetails] = useState(false);
  const [showApplicationForm, setShowApplicationForm] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    education: '',
    job: '',
    experience: '',
    currentCompany: '',
    resume: null,
    coverLetter: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [resumeUploading, setResumeUploading] = useState(false);
  const [resumeFileName, setResumeFileName] = useState('');

  // Fetch active jobs on component mount
  useEffect(() => {
    const fetchJobs = async () => {
      try {
        setLoading(true);
        const response = await fetch(SummaryApi.getActiveJobOpenings.url, {
          method: SummaryApi.getActiveJobOpenings.method,
          credentials: 'include'
        });
        
        const data = await response.json();
        
        if (response.ok) {
          setJobs(data);
        } else {
          toast.error(data.message || 'Failed to fetch job openings');
        }
      } catch (error) {
        toast.error('Error fetching job openings');
        console.error('Error:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchJobs();
  }, []);

  // Capitalize first letter of each word
  const capitalize = (str) => {
    if (!str) return '';
    return str.replace(/\b\w/g, char => char.toUpperCase());
  };

  // Capitalize sentence (first letter only)
  const capitalizeSentence = (str) => {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value, files } = e.target;
    
    if (name === 'resume' && files && files[0]) {
      // Validate file size (5MB limit)
      if (files[0].size > 5 * 1024 * 1024) {
        toast.error('File size must be less than 5MB');
        return;
      }
      
      // Validate file type
      const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (!allowedTypes.includes(files[0].type)) {
        toast.error('Only PDF, DOC, and DOCX files are allowed');
        return;
      }
      
      setFormData({ ...formData, [name]: files[0] });
      setResumeFileName(files[0].name);
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  // Handle job card click to view details
  const handleJobClick = (job) => {
    setSelectedJob(job);
    setShowJobDetails(true);
    setShowApplicationForm(false);
  };

  // Handle apply button click
  const handleApplyClick = (job) => {
    setSelectedJob(job);
    setFormData({ ...formData, job: job._id });
    setShowJobDetails(false);
    setShowApplicationForm(true);
    // Scroll to form
    setTimeout(() => {
      document.getElementById('career-form')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Validate required fields
      if (!formData.firstName || !formData.lastName || !formData.email || !formData.phone || !formData.job || !formData.resume) {
        toast.error('Please fill all required fields');
        setIsSubmitting(false);
        return;
      }
      
      // Upload resume to S3
      let resumeData = null;
      if (formData.resume) {
        setResumeUploading(true);
        resumeData = await uploadResumeToS3(formData.resume);
        setResumeUploading(false);
        
        if (!resumeData) {
          toast.error('Failed to upload resume. Please try again.');
          setIsSubmitting(false);
          return;
        }
      }
      
      // Prepare application data
      const applicationData = {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        coverLetter: formData.coverLetter.trim(),
        resume: {
          url: resumeData.url,
          originalName: resumeData.originalName
        },
        job: formData.job
      };

      // Get auth token if available (optional)
      const token = localStorage.getItem("authToken");
      
      // Prepare headers
      const headers = {
        "Content-Type": "application/json"
      };
      
      // Add auth token if available
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }

      // Send application to backend
      const response = await fetch(SummaryApi.createApplication.url, {
        method: SummaryApi.createApplication.method,
        headers: headers,
        body: JSON.stringify(applicationData)
      });

      const responseData = await response.json();

      if (response.ok) {
        toast.success('Application submitted successfully!');
        // Reset form
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          education: '',
          job: '',
          experience: '',
          currentCompany: '',
          resume: null,
          coverLetter: ''
        });
        setResumeFileName('');
        setSelectedJob(null);
        setShowApplicationForm(false);
      } else {
        toast.error(responseData.message || 'Failed to submit application. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting application:', error);
      
      // Handle network errors
      if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
        toast.error('Network error. Please check your connection and try again.');
      } else {
        toast.error(error.message || 'Failed to submit application. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'No deadline';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  // Get priority color
  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'Urgent': return 'bg-red-100 text-red-800 border border-red-200';
      case 'High': return 'bg-orange-100 text-orange-800 border border-orange-200';
      case 'Medium': return 'bg-yellow-100 text-yellow-800 border border-yellow-200';
      case 'Low': return 'bg-green-100 text-green-800 border border-green-200';
      default: return 'bg-gray-100 text-gray-800 border border-gray-200';
    }
  };

  // Get work mode icon
  const getWorkModeIcon = (mode) => {
    switch (mode) {
      case 'Remote': return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"></path>
        </svg>
      );
      case 'On-Site': return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"></path>
        </svg>
      );
      case 'Hybrid': return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9"></path>
        </svg>
      );
      default: return null;
    }
  };

  return (
    <section
      className="bg-gradient-to-br from-blue-900 via-blue-800 to-indigo-900 py-16 sm:py-20 mt-16 rounded-br-[120px] rounded-tl-[120px] md:rounded-br-[180px] md:rounded-tl-[180px]"
      aria-label="Career opportunity section"
    >
      <div className="container mx-auto px-4 sm:px-6 md:px-8 text-white">
        {/* Header Section */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-white to-blue-200 bg-clip-text text-transparent">
            Join Our Team
          </h2>
          <p className="text-xl text-blue-200 max-w-2xl mx-auto">
            Discover exciting career opportunities and be part of our innovative team
          </p>
        </div>

        {/* Loading State */}
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-white"></div>
            <span className="ml-3 text-lg">Loading opportunities...</span>
          </div>
        ) : (
          <>
            {/* Jobs Grid */}
            {jobs.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
                {jobs.map((job) => (
                  <div
                    key={job._id}
                    className="bg-white/10 backdrop-blur-sm border border-white/20 p-6 rounded-2xl shadow-2xl transform hover:scale-105 transition-all duration-300 cursor-pointer hover:bg-white/20 group"
                    onClick={() => handleJobClick(job)}
                  >
                    {/* Job Header */}
                    <div className="flex justify-between items-start mb-4">
                      <h3 className="text-xl font-bold text-white group-hover:text-blue-200 transition-colors">
                        {capitalize(job.title)}
                      </h3>
                      <span className={`px-3 py-1 text-xs font-semibold rounded-full ${getPriorityColor(job.priority)}`}>
                        {job.priority}
                      </span>
                    </div>
                    
                    {/* Department */}
                    <p className="text-blue-200 font-medium mb-3">{capitalize(job.department)}</p>
                    
                    {/* Description */}
                    <p className="text-white/80 text-sm mb-4 line-clamp-3">
                      {capitalizeSentence(job.description)}
                    </p>
                    
                    {/* Work Mode & Location */}
                    <div className="flex items-center text-sm text-blue-200 mb-3">
                      <div className="flex items-center">
                        {getWorkModeIcon(job.workMode)}
                        <span className="ml-2 font-medium">{job.workMode}</span>
                      </div>
                      <span className="mx-3 text-white/50">•</span>
                      <span>{capitalize(job.location)}</span>
                    </div>
                    
                    {/* Experience */}
                    <div className="flex items-center text-sm text-blue-200 mb-4">
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
                      </svg>
                      {capitalize(job.experienceLevel)}
                      {job.experienceYears && job.experienceYears.min > 0 && (
                        <span className="ml-1">({job.experienceYears.min}-{job.experienceYears.max} years)</span>
                      )}
                    </div>
                    
                    {/* Deadline */}
                    <div className="flex items-center text-sm text-blue-200 mb-4">
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                      </svg>
                      Deadline: {formatDate(job.applicationDeadline)}
                    </div>
                    
                    {/* Skills */}
                    {job.requiredSkills && job.requiredSkills.length > 0 && (
                      <div className="mb-4">
                        <p className="text-sm font-medium text-blue-200 mb-2">Key Skills:</p>
                        <div className="flex flex-wrap gap-2">
                          {job.requiredSkills.slice(0, 3).map((skill, index) => (
                            <span key={index} className="px-3 py-1 bg-blue-600/30 text-blue-100 rounded-full text-xs font-medium border border-blue-500/30">
                              {capitalize(skill)}
                            </span>
                          ))}
                          {job.requiredSkills.length > 3 && (
                            <span className="px-3 py-1 bg-white/20 text-white rounded-full text-xs font-medium">
                              +{job.requiredSkills.length - 3} more
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {/* CTA */}
                    <div className="text-center pt-4 border-t border-white/20">
                      <span className="text-blue-200 font-semibold text-sm group-hover:text-white transition-colors">
                        View Details & Apply
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-16 bg-white/10 backdrop-blur-sm rounded-2xl border border-white/20">
                <div className="max-w-md mx-auto">
                  <svg className="w-16 h-16 text-blue-200 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                  <h3 className="text-2xl font-bold text-white mb-2">No Current Openings</h3>
                  <p className="text-blue-200 text-lg mb-4">
                    We don't have any job openings available at the moment.
                  </p>
                  <p className="text-white/80">
                    Please check back later or follow us on social media for updates on new opportunities.
                  </p>
                </div>
              </div>
            )}

            {/* Job Details Modal */}
            {showJobDetails && selectedJob && (
              <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 overflow-y-auto p-4">
                <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
                  <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-indigo-700 text-white px-6 py-4 flex justify-between items-center rounded-t-2xl">
                    <h2 className="text-2xl font-bold">Job Details</h2>
                    <button
                      onClick={() => setShowJobDetails(false)}
                      className="text-white hover:text-blue-200 transition-colors p-1 rounded-full hover:bg-white/20"
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                      </svg>
                    </button>
                  </div>

                  <div className="p-6">
                    {/* Job Header */}
                    <div className="mb-8">
                      <div className="flex flex-col md:flex-row md:justify-between md:items-start mb-4 gap-4">
                        <h3 className="text-3xl font-bold text-gray-800">{capitalize(selectedJob.title)}</h3>
                        <span className={`px-4 py-2 text-sm font-semibold rounded-full ${getPriorityColor(selectedJob.priority)} self-start`}>
                          {selectedJob.priority} Priority
                        </span>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm text-gray-600">
                        <div className="flex items-center bg-blue-50 p-3 rounded-lg">
                          <svg className="w-5 h-5 mr-3 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"></path>
                          </svg>
                          <span className="font-medium">{capitalize(selectedJob.department)}</span>
                        </div>
                        
                        <div className="flex items-center bg-green-50 p-3 rounded-lg">
                          {getWorkModeIcon(selectedJob.workMode)}
                          <span className="ml-3 font-medium">{selectedJob.workMode}</span>
                        </div>
                        
                        <div className="flex items-center bg-purple-50 p-3 rounded-lg">
                          <svg className="w-5 h-5 mr-3 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path>
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path>
                          </svg>
                          <span className="font-medium">{capitalize(selectedJob.location)}</span>
                        </div>
                        
                        <div className="flex items-center bg-yellow-50 p-3 rounded-lg">
                          <svg className="w-5 h-5 mr-3 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                          </svg>
                          <span className="font-medium">Deadline: {formatDate(selectedJob.applicationDeadline)}</span>
                        </div>
                      </div>
                    </div>

                    {/* Job Description */}
                    <div className="mb-8">
                      <h4 className="text-xl font-semibold text-gray-800 mb-4 pb-2 border-b-2 border-blue-200">Job Description</h4>
                      <p className="text-gray-600 leading-relaxed whitespace-pre-line">
                        {capitalizeSentence(selectedJob.description)}
                      </p>
                    </div>

                    {/* Two Column Layout */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                      {/* Left Column */}
                      <div className="space-y-8">
                        {/* Responsibilities */}
                        {selectedJob.responsibilities && selectedJob.responsibilities.length > 0 && (
                          <div>
                            <h4 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                              <svg className="w-5 h-5 mr-2 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                              </svg>
                              Key Responsibilities
                            </h4>
                            <ul className="space-y-2">
                              {selectedJob.responsibilities.map((responsibility, index) => (
                                <li key={index} className="flex items-start text-gray-600">
                                  <span className="text-green-500 mr-2 mt-1">•</span>
                                  <span>{capitalizeSentence(responsibility)}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {/* Requirements */}
                        {selectedJob.requirements && selectedJob.requirements.length > 0 && (
                          <div>
                            <h4 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                              <svg className="w-5 h-5 mr-2 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
                              </svg>
                              Requirements
                            </h4>
                            <ul className="space-y-2">
                              {selectedJob.requirements.map((requirement, index) => (
                                <li key={index} className="flex items-start text-gray-600">
                                  <span className="text-red-500 mr-2 mt-1">•</span>
                                  <span>{capitalizeSentence(requirement)}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>

                      {/* Right Column */}
                      <div className="space-y-8">
                        {/* Skills */}
                        <div className="space-y-6">
                          {selectedJob.requiredSkills && selectedJob.requiredSkills.length > 0 && (
                            <div>
                              <h4 className="text-lg font-semibold text-gray-800 mb-3">Required Skills</h4>
                              <div className="flex flex-wrap gap-2">
                                {selectedJob.requiredSkills.map((skill, index) => (
                                  <span key={index} className="px-3 py-2 bg-blue-100 text-blue-800 rounded-full text-sm font-medium border border-blue-200">
                                    {capitalize(skill)}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}

                          {selectedJob.preferredSkills && selectedJob.preferredSkills.length > 0 && (
                            <div>
                              <h4 className="text-lg font-semibold text-gray-800 mb-3">Preferred Skills</h4>
                              <div className="flex flex-wrap gap-2">
                                {selectedJob.preferredSkills.map((skill, index) => (
                                  <span key={index} className="px-3 py-2 bg-gray-100 text-gray-800 rounded-full text-sm font-medium border border-gray-200">
                                    {capitalize(skill)}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Details Grid */}
                        <div className="grid grid-cols-1 gap-4">
                          <div className="bg-gray-50 p-4 rounded-lg">
                            <h4 className="font-semibold text-gray-800 mb-2">Experience</h4>
                            <p className="text-gray-600">
                              {capitalize(selectedJob.experienceLevel)}
                              {selectedJob.experienceYears && selectedJob.experienceYears.min > 0 && (
                                <span> ({selectedJob.experienceYears.min}-{selectedJob.experienceYears.max} years)</span>
                              )}
                            </p>
                          </div>

                          {selectedJob.qualifications && selectedJob.qualifications.length > 0 && (
                            <div className="bg-gray-50 p-4 rounded-lg">
                              <h4 className="font-semibold text-gray-800 mb-2">Qualifications</h4>
                              <ul className="space-y-1 text-gray-600">
                                {selectedJob.qualifications.map((qualification, index) => (
                                  <li key={index} className="flex items-start">
                                    <span className="text-blue-500 mr-2 mt-1">•</span>
                                    <span>{capitalizeSentence(qualification)}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}

                          <div className="bg-gray-50 p-4 rounded-lg">
                            <h4 className="font-semibold text-gray-800 mb-2">Position Details</h4>
                            <div className="space-y-2 text-gray-600">
                              <div className="flex justify-between">
                                <span>Job Type:</span>
                                <span className="font-medium">{capitalize(selectedJob.jobType)}</span>
                              </div>
                              <div className="flex justify-between">
                                <span>Openings:</span>
                                <span className="font-medium">{selectedJob.openings} position{selectedJob.openings > 1 ? 's' : ''}</span>
                              </div>
                              {selectedJob.salaryRange && selectedJob.salaryRange.min > 0 && selectedJob.salaryRange.max > 0 && (
                                <div className="flex justify-between">
                                  <span>Salary Range:</span>
                                  <span className="font-medium">${selectedJob.salaryRange.min} - ${selectedJob.salaryRange.max}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Perks and Benefits */}
                    {selectedJob.perksAndBenefits && selectedJob.perksAndBenefits.length > 0 && (
                      <div className="mt-8">
                        <h4 className="text-xl font-semibold text-gray-800 mb-4 pb-2 border-b-2 border-green-200">Perks and Benefits</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {selectedJob.perksAndBenefits.map((perk, index) => (
                            <div key={index} className="flex items-center bg-green-50 p-3 rounded-lg">
                              <svg className="w-5 h-5 text-green-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                              </svg>
                              <span className="text-gray-700">{capitalizeSentence(perk)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Action Buttons */}
                    <div className="flex flex-col sm:flex-row justify-end space-y-3 sm:space-y-0 sm:space-x-4 mt-8 pt-6 border-t border-gray-200">
                      <button
                        onClick={() => setShowJobDetails(false)}
                        className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                      >
                        Close Details
                      </button>
                      <button
                        onClick={() => handleApplyClick(selectedJob)}
                        className="px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-700 border border-transparent rounded-lg font-medium text-white hover:from-blue-700 hover:to-indigo-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-300 transform hover:scale-105"
                      >
                        Apply For This Position
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Application Form */}
            {showApplicationForm && (
              <div id="career-form" className="mt-20 bg-white/10 backdrop-blur-sm border border-white/20 max-w-4xl mx-auto p-8 rounded-2xl shadow-2xl text-white">
                <div className="text-center mb-8">
                  <h3 className="text-3xl font-bold mb-2 bg-gradient-to-r from-white to-blue-200 bg-clip-text text-transparent">
                    Apply for {selectedJob?.title}
                  </h3>
                  <p className="text-blue-200">Complete the form below to submit your application</p>
                </div>
                
                {selectedJob && (
                  <div className="mb-8 p-6 bg-white/10 rounded-xl border border-white/20">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                      <div>
                        <p className="font-bold text-xl">{capitalize(selectedJob.title)}</p>
                        <p className="text-blue-200">{capitalize(selectedJob.department)} • {capitalize(selectedJob.location)}</p>
                      </div>
                      <button
                        onClick={() => {
                          setSelectedJob(null);
                          setShowApplicationForm(false);
                          setFormData({ ...formData, job: '' });
                          setResumeFileName('');
                        }}
                        className="text-white hover:text-blue-200 transition-colors p-2 rounded-full hover:bg-white/20"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                        </svg>
                      </button>
                    </div>
                  </div>
                )}
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block mb-2 font-semibold">First Name *</label>
                      <input
                        type="text"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 bg-white/10 border border-white/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent placeholder-white/60 text-white"
                        placeholder="Enter your first name"
                      />
                    </div>

                    <div>
                      <label className="block mb-2 font-semibold">Last Name *</label>
                      <input
                        type="text"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 bg-white/10 border border-white/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent placeholder-white/60 text-white"
                        placeholder="Enter your last name"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block mb-2 font-semibold">Email Address *</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 bg-white/10 border border-white/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent placeholder-white/60 text-white"
                        placeholder="Enter your email"
                      />
                    </div>

                    <div>
                      <label className="block mb-2 font-semibold">Mobile Number *</label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 bg-white/10 border border-white/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent placeholder-white/60 text-white"
                        placeholder="Enter your mobile number"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block mb-2 font-semibold">Education *</label>
                      <input
                        type="text"
                        name="education"
                        value={formData.education}
                        onChange={handleChange}
                        placeholder="e.g. B.Tech in Computer Science"
                        required
                        className="w-full px-4 py-3 bg-white/10 border border-white/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent placeholder-white/60 text-white"
                      />
                    </div>

                    <div>
                      <label className="block mb-2 font-semibold">Experience (years)</label>
                      <input
                        type="text"
                        name="experience"
                        value={formData.experience}
                        onChange={handleChange}
                        placeholder="e.g. 2 years"
                        className="w-full px-4 py-3 bg-white/10 border border-white/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent placeholder-white/60 text-white"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block mb-2 font-semibold">Current Company</label>
                    <input
                      type="text"
                      name="currentCompany"
                      value={formData.currentCompany}
                      onChange={handleChange}
                      placeholder="e.g. ABC Tech Solutions"
                      className="w-full px-4 py-3 bg-white/10 border border-white/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent placeholder-white/60 text-white"
                    />
                  </div>

                  <div>
                    <label className="block mb-2 font-semibold">Resume/CV *</label>
                    <input
                      type="file"
                      name="resume"
                      onChange={handleChange}
                      accept=".pdf,.doc,.docx"
                      required
                      className="w-full px-4 py-3 bg-white/10 border border-white/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:bg-white/20 file:text-white file:font-semibold hover:file:bg-white/30"
                    />
                    <p className="text-white/60 text-sm mt-2">
                      {resumeFileName ? `Selected: ${resumeFileName}` : 'Accepted formats: PDF, DOC, DOCX (Max 5MB)'}
                    </p>
                  </div>

                  <div>
                    <label className="block mb-2 font-semibold">Cover Letter</label>
                    <textarea
                      name="coverLetter"
                      value={formData.coverLetter}
                      onChange={handleChange}
                      rows="5"
                      placeholder="Tell us why you're interested in this position and what makes you a great candidate..."
                      className="w-full px-4 py-3 bg-white/10 border border-white/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent placeholder-white/60 text-white resize-none"
                    ></textarea>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting || resumeUploading}
                    className="w-full bg-gradient-to-r from-blue-600 to-indigo-700 text-white py-4 rounded-xl font-bold text-lg hover:from-blue-700 hover:to-indigo-800 focus:outline-none focus:ring-4 focus:ring-blue-400 focus:ring-opacity-50 transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                  >
                    {isSubmitting || resumeUploading ? (
                      <div className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-white mr-3"></div>
                        {resumeUploading ? 'Uploading Resume...' : 'Submitting Application...'}
                      </div>
                    ) : (
                      'Submit Application'
                    )}
                  </button>
                </form>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
};

export default Career;